import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { routePermissions } from "route-permissions";
import { useAuthLoggedIn, useAuthTenant, useAuthUser } from "store/auth";
import { useSelector } from "react-redux";

import { AcceptPolicyVersion } from "Pages/AcceptPolicyVersion";
import { Areas } from "Pages/Areas";
import { AreasForm } from "Pages/Areas/Form";
import { AreasShow } from "Pages/Areas/Show";
import { Assets } from "Pages/Assets";
import { AssetsForm } from "Pages/Assets/Form";
import { AssetsShow } from "Pages/Assets/Show";
import { CreateAccount } from "Pages/CreateAccount";
import { Companies } from "Pages/Companies";
import { CompaniesForm } from "Pages/Companies/Form";
import { CompaniesShow } from "Pages/Companies/Show";
import { CompaniesPartner } from "Pages/CompaniesPartner";
import { CompaniesPartnerForm } from "Pages/CompaniesPartner/Form";
import { CompaniesPartnerShow } from "Pages/CompaniesPartner/Show";
import { CompanyProfile } from "Pages/CompanyProfile";
import { Cookies } from "Pages/Cookies";
import { CookiesForm } from "Pages/Cookies/Form";
import { CookiesShow } from "Pages/Cookies/Show";
import { Dashboard } from "Pages/Dashboard";
import { AsmDashboard } from "Pages/AsmDashboard";
import { GapAnalysisForm } from 'Pages/GapAnalysis/Form';
import { GapAnalysisShow } from "Pages/GapAnalysis/Show";
import { GapAnalysisControlDetalShow } from "Pages/GapAnalysis/Show/ControlDetal";
import { GapAnalysis } from "Pages/GapAnalysis";
import { GapAnalysisDashboard } from "Pages/GapAnalysis/Dashboard"
import { Data } from "Pages/Data";
import { DataForm } from "Pages/Data/Form";
import { DataShow } from "Pages/Data/Show";
import ErrorBoundary from "Components/ErrorBoundary";
import { hasPermission } from "utils/permission_utils";
import { Login } from "Pages/Login";
import { Map } from "Pages/Map";
import { NoPermission } from "Pages/NoPermission";
import { Offices } from "./Pages/Offices";
import { OnboardCompany } from "Pages/Onboard/Company";
import { OnboardCompanyAdmin } from "Pages/Onboard/CompanyAdmin";
import { OnboardChoosePlan } from "Pages/Onboard/ChoosePlan";
import { OnboardCancel } from "Pages/Onboard/Cancel";
import { OnboardSuccess } from "Pages/Onboard/Success";
import { OfficesForm } from "Pages/Offices/Form";
import { OfficesShow } from "Pages/Offices/Show";
import { PoliciesShow } from "Pages/Policies/Show";
import { PoliciesChecklistVersion } from "Pages/Policies/ChecklistVersion";
import { Processes } from "Pages/Processes";
import { ProcessesForm } from "Pages/Processes/Form";
import { ProcessesShow } from "Pages/Processes/Show";
import { Policies } from "Pages/Policies";
import { PoliciesForm } from "Pages/Policies/Form";
import { ProcessesDatasShow } from "Pages/Processes/Show/Datas";
import ReportsRopa from "Pages/Reports/Ropa";
import { ReportsRopaForm } from "Pages/Reports/Ropa/Form";
import { ReportsRopaShow } from "Pages/Reports/Ropa/Show";
import { Systems } from "Pages/Systems";
import { SystemsForm } from "Pages/Systems/Form";
import { SystemsShow } from "Pages/Systems/Show";
import { Users } from "Pages/Users";
import { UsersForm } from "Pages/Users/Form";
import { UsersGroups } from "Pages/UsersGroups";
import { UsersGroupsForm } from "Pages/UsersGroups/Form";
import { UsersGroupsShow } from "Pages/UsersGroups/Show";
import { UsersShow } from "Pages/Users/Show";
// import { UsersPermissions } from 'Pages/UsersPermissions'
import { VerifyCode } from "Pages/VerifyCode";
import { DataMapping } from "Pages/Processes/Form/DataMapping";
import { AuditLogs } from "Pages/AuditLogs";
import { PolicyVersions } from "Pages/Policies/Versions";
import { PolicyVersionForm } from "Pages/Policies/Versions/Form";
import { PolicyVersionShow } from "Pages/Policies/Versions/Show";
import { PoliciesShowAccepts } from "Pages/Policies/Show/Accepts";
import { GapAnalysisControlLinkShow } from './Pages/GapAnalysis/Show/ControlLink';
import { GapAnalysisControlsLinkShow } from './Pages/GapAnalysis/Show/ControlsLink';
import { UsersVerifyCodes } from "Pages/UsersVerifyCodes";
import { UserNotifications } from "Pages/UserNotifications";
import { ComplianceProgramForm } from './Pages/ComplianceProgram/Form'
import { ComplianceProgramShow } from './Pages/ComplianceProgram/Show';
import { ComplianceProgramDashboard } from './Pages/ComplianceProgram/Dashboard';
import GapAnalysisReports  from './Pages/Reports/GapAnalysis';
import { ReportsGapAnalysisForm }  from './Pages/Reports/GapAnalysis/Form';
import { GapAnalysisReportDashboard } from './Pages/Reports/GapAnalysis/Show';
import { OpsDashboard } from 'Pages/OpsDashboard';
import { TransparencyPage } from './Pages/TransparencyCenter';
import { ProtectedTransparencyView } from './Pages/TransparencyCenter/Protected';
import { TransparencyPreview } from './Pages/TransparencyCenter/Preview';
import { TransparencyConfig } from './Pages/TransparencyCenter/Config';

// Vendor Assessment Imports - T117, T118, T119
import { VendorsPage } from 'Pages/vendors/VendorsPage';
import { VendorDetail } from 'Components/vendors/VendorDetail';
import { AssessmentsPage } from 'Pages/assessments/AssessmentsPage';
import { AssessmentWizardPage } from 'Pages/assessments/AssessmentWizardPage';
import { AssessmentDetail } from 'Components/assessments/AssessmentDetail';
import { VendorAssessmentForm } from 'Components/assessments/VendorAssessmentForm';
import { DashboardPage as VendorDashboardPage } from 'Pages/Dashboard/DashboardPage';
import { BrandingSettings } from 'Pages/Settings/Branding';

// Scanner (CSPM) - Fase 4
import { ScannerDashboard } from 'Pages/Scanner/Dashboard';
import { ScannerAccounts } from 'Pages/Scanner/Accounts';
import { ScannerAccountNew } from 'Pages/Scanner/Accounts/New';
import { ScannerScans } from 'Pages/Scanner/Scans';
import { ScannerScanShow } from 'Pages/Scanner/Scans/Show';
import { ScannerFindings } from 'Pages/Scanner/Findings';
import { ScannerResources } from 'Pages/Scanner/Resources';
import { AdminScannerBatch } from 'Pages/AdminScanner/Batch';
import { AdminScannerBatchShow } from 'Pages/AdminScanner/Batch/Show';
import { AdminScannerFindings } from 'Pages/AdminScanner/Findings';

const Routing = () => {
  const isLoggedIn = useSelector(useAuthLoggedIn);
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);

  const ProtectedRoute = ({
    isLoggedIn,
    onlyAdminVO = false,
    path,
    children,
  }) => {
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }
    const routePermission = routePermissions.find(
      (route) => route.path === path
    );
    if (
      routePermission &&
      !hasPermission(user.permissions, routePermission.permission) &&
      !['super_admin_global', 'admin_global'].includes(user.roleActive)
    ) {
      return <Navigate to="/no-permission" replace />;
    }

    if (
      onlyAdminVO &&
      (!["super_admin_global", "admin_global"].includes(user.roleActive) ||
        selectedTenant.slug !== "vo")
    ) {
      return <Navigate to="/no-permission" replace />;
    }

    return (
      <ErrorBoundary
        fallback={(error) => {
          return <p>{error.message}</p>;
        }}
      >
        {children}
      </ErrorBoundary>
    );
  };

  return (
    <Routes>
      <Route
        exact
        path="/"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/dashboard">
            <Navigate to="/dashboard" />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/asm"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/asm">
            <AsmDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/areas"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/areas">
            <Areas />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/areas/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/areas/add">
            <AreasForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/areas/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/areas/edit">
            <AreasForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/areas/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/areas/show">
            <AreasShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/assets"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/assets">
            <Assets />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/assets/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/assets/add">
            <AssetsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/assets/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/assets/edit">
            <AssetsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/assets/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/assets/show">
            <AssetsShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/audit-logs"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/audit-logs">
            <AuditLogs />
          </ProtectedRoute>
        }
      />
      <Route exact path="/create-account" element={<CreateAccount />} />
      <Route
        exact
        path="/cookies"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/cookies">
            <Cookies />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/cookies/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/cookies/add">
            <CookiesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/cookies/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/cookies/edit">
            <CookiesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/cookies/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/cookies/show">
            <CookiesShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/onboard-company/:idOnboard"
        element={<OnboardCompany />}
      />
      <Route
        exact
        path="/onboard-company-admin/:slug"
        element={<OnboardCompanyAdmin />}
      />
      <Route
        exact
        path="/onboard-choose-plan/:slug"
        element={<OnboardChoosePlan />}
      />
      <Route
        exact
        path="/onboard-success/:slug/:plan"
        element={<OnboardSuccess />}
      />
      <Route
        exact
        path="/onboard-cancel/:slug"
        element={<OnboardCancel />}
      />
      <Route
        exact
        path="/company-profile"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/company-profile">
            <CompanyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/settings/branding"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/settings/branding">
            <BrandingSettings />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/companies">
            <Companies />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies/add"
        element={
          <ProtectedRoute
            isLoggedIn={isLoggedIn}
            onlyAdminVO
            path="/companies/add"
          >
            <CompaniesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies/edit/:id"
        element={
          <ProtectedRoute
            isLoggedIn={isLoggedIn}
            onlyAdminVO
            path="/companies/edit"
          >
            <CompaniesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies/show/:id"
        element={
          <ProtectedRoute
            isLoggedIn={isLoggedIn}
            onlyAdminVO
            path="/companies/show"
          >
            <CompaniesShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies-partner"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/companies-partner">
            <CompaniesPartner />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies-partner/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/companies-partner/add">
            <CompaniesPartnerForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies-partner/edit/:id"
        element={
          <ProtectedRoute
            isLoggedIn={isLoggedIn}
            path="/companies-partner/edit"
          >
            <CompaniesPartnerForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/companies-partner/show/:id"
        element={
          <ProtectedRoute
            isLoggedIn={isLoggedIn}
            path="/companies-partner/show"
          >
            <CompaniesPartnerShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/data"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/data">
            <Data />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/data/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/data/add">
            <DataForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/data/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/data/edit">
            <DataForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/data/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/data/show">
            <DataShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/dashboard"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/gap-analysis"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis">
            <GapAnalysis />
          </ProtectedRoute>
        }
      />

      <Route
        exact
        path="/gap-analysis/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/add">
            <GapAnalysisForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/gap-analysis/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/show">
            <GapAnalysisShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/gap-analysis/control/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <GapAnalysisControlDetalShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/gap-analysis/:id/vincular/:control_id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <GapAnalysisControlLinkShow />
          </ProtectedRoute>
        }
      />

      <Route
        exact
        path="/gap-analysis/show/:id/vincular"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <GapAnalysisControlsLinkShow />
          </ProtectedRoute>
        }
      />

      <Route
        exact
        path="/gap-analysis/dashboard/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <GapAnalysisDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/gap-analysis"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/gap-analysis">
            <GapAnalysisReports />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/gap-analysis/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/gap-analysis/add">
            <ReportsGapAnalysisForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/gap-analysis/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/gap-analysis/show">
            <GapAnalysisReportDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/compliance-program/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <ComplianceProgramForm />
          </ProtectedRoute>
        }
      />

      <Route
        exact
        path="/compliance-program"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/gap-analysis/control/show">
            <ComplianceProgramShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/compliance-program/control/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/compliance-program/control/show">
            <GapAnalysisControlDetalShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/compliance-program/:id/vincular/:control_id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/compliance-program/control/show">
            <GapAnalysisControlLinkShow />
          </ProtectedRoute>
        }
      />

      <Route
        exact
        path="/compliance-program/show/:id/vincular"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/compliance-program/control/show">
            <GapAnalysisControlsLinkShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/compliance-program"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/compliance-program">
            <ComplianceProgramDashboard />
          </ProtectedRoute>
        }
      />
      <Route exact path="/login" element={<Login />} />
      <Route
        exact
        path="/map"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/map">
            <Map />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/offices"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/offices">
            <Offices />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/offices/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/offices">
            <OfficesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/offices/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/offices">
            <OfficesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/offices/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/offices">
            <OfficesShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies">
            <Policies />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/add">
            <PoliciesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/checklist/:id/versions/:idVersion"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/edit">
            <PoliciesChecklistVersion />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/edit">
            <PoliciesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/show">
            <PoliciesShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/show/:id/versions/:idVersion"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/show">
            <PolicyVersionShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/show/:id/versions/:idVersion/accepts"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/show/:id/versions/:idVersion/accepts">
            <PoliciesShowAccepts />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/:id/versions"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/edit">
            <PolicyVersions />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/:id/versions/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/edit">
            <PolicyVersionForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/policies/:id/versions/edit/:idVersion"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/policies/edit">
            <PolicyVersionForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes">
            <Processes />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/add">
            <ProcessesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/edit">
            <ProcessesForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/edit/:id/mapping"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/edit">
            <DataMapping />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/edit/:id/mapping/:mappingId"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/edit">
            <DataMapping />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/show">
            <ProcessesShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/show/:id/datas"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/show">
            <ProcessesDatasShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/processes/show/:id/mapping/:mappingId"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/processes/show">
            <ProcessesDatasShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/systems"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/systems">
            <Systems />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/systems/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/systems/add">
            <SystemsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/systems/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/systems/edit">
            <SystemsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/systems/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/systems/show">
            <SystemsShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users">
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users/add">
            <UsersForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users/edit">
            <UsersForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users/show">
            <UsersShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users-groups"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users-groups">
            <UsersGroups />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users-groups/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users-groups/add">
            <UsersGroupsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users-groups/edit/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users-groups/edit">
            <UsersGroupsForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users-groups/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/users-groups/show">
            <UsersGroupsShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/accept-policy-version/:idPolicyVersionUser"
        element={
          <ErrorBoundary
            fallback={(error) => <p>Ocorreu um erro: {error.message}</p>}
          >
            <AcceptPolicyVersion />
          </ErrorBoundary>
        }
      />
      <Route
        exact
        path="/verify-code/:email"
        element={
          <ErrorBoundary
            fallback={(error) => <p>Ocorreu um erro: {error.message}</p>}
          >
            <VerifyCode />
          </ErrorBoundary>
        }
      />
      <Route
        exact
        path="/reports/ropa"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/ropa">
            <ReportsRopa />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/ropa/add"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/ropa/add">
            <ReportsRopaForm />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/reports/ropa/show/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/reports/ropa">
            <ReportsRopaShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/users-verify-codes"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/users-verify-codes">
            <UsersVerifyCodes />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/user-notifications"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/user-notifications">
            <UserNotifications />
          </ProtectedRoute>
        }
      />
      <Route exact path="no-permission" element={<NoPermission />} />

      {/* Transparency Center Routes */}
      <Route
        path="/transparency/:companyKey"
        element={
          <ErrorBoundary
            fallback={(error) => <p>Ocorreu um erro: {error.message}</p>}
          >
            <TransparencyPage />
          </ErrorBoundary>
        }
      />
      <Route
        exact
        path="/transparency-center"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/transparency-center">
            <ProtectedTransparencyView />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/transparency-center/preview"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/transparency-center/preview">
            <TransparencyPreview />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/transparency-center/config"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/transparency-center/config">
            <TransparencyConfig />
          </ProtectedRoute>
        }
      />

      {/* Operations Dashboard - Global Admin only */}
      <Route
        exact
        path="/ops-dashboard"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/ops-dashboard">
            <OpsDashboard />
          </ProtectedRoute>
        }
      />

      {/* Vendor Management Routes - T117 */}
      <Route
        exact
        path="/vendors"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendors">
            <VendorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/vendors/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendors/:id">
            <VendorDetail />
          </ProtectedRoute>
        }
      />

      {/* Assessment Management Routes - T118 */}
      <Route
        exact
        path="/vendor-assessments"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendor-assessments">
            <AssessmentsPage />
          </ProtectedRoute>
        }
      />

      {/* Assessment Wizard Page - T118 */}
      <Route
        exact
        path="/vendor-assessments/new"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendor-assessments/new">
            <AssessmentWizardPage />
          </ProtectedRoute>
        }
      />

      {/* Assessment Detail Route - T118 */}
      <Route
        exact
        path="/vendor-assessments/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendor-assessments/:id">
            <AssessmentDetail />
          </ProtectedRoute>
        }
      />

      {/* PUBLIC ROUTE - Vendor Assessment Submission (NO AUTHENTICATION) - T118 */}
      <Route
        exact
        path="/vendor-assessment/:token"
        element={<VendorAssessmentForm />}
      />

      {/* Executive Dashboard Route - T119 */}
      <Route
        exact
        path="/vendor-dashboard"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/vendor-dashboard">
            <VendorDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Scanner (CSPM) - Tenant routes */}
      <Route
        exact
        path="/scanner/dashboard"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/findings"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerFindings />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/accounts"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerAccounts />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/accounts/new"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerAccountNew />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/scans"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerScans />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/scans/:id"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerScanShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/scanner/resources"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} path="/scanner/accounts">
            <ScannerResources />
          </ProtectedRoute>
        }
      />

      {/* Scanner (CSPM) - Admin routes */}
      <Route
        exact
        path="/admin/scanner/batch"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/admin/scanner/batch">
            <AdminScannerBatch />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/admin/scanner/batch/:batchId"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/admin/scanner/batch">
            <AdminScannerBatchShow />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path="/admin/scanner/findings"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} onlyAdminVO path="/admin/scanner/findings">
            <AdminScannerFindings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default Routing;
